import { BarChart3 } from "lucide-react";
import { PageHeading, Section, Callout, RoleBadge, Diagram, MockRow, MockSelect, MockKpi } from "../shared";

export default function AdminKpiSection() {
  return (
    <div data-testid="guide-admin-kpi">
      <PageHeading
        icon={BarChart3}
        title="KPI Dashboard"
        description="A read-only scoreboard inside the Admin page that shows how each person is performing. You can't break anything here — you just pick a date range and read the numbers."
      />

      <Callout variant="info" title="In plain words">
        This is a performance snapshot. Pick a time window and the screen shows, person by person, how much work they handled, how long things took, and where deadlines were missed. There are no edit buttons — only filters and figures.
      </Callout>

      <Section title="Who can see this tab">
        <div className="flex items-center gap-2"><RoleBadge role="admin" /><span className="text-xs text-white/55">Any role granted the <span className="text-white">view_kpi_dashboard</span> permission. By default: General Admin and above.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it for a quick health check — who is carrying the most work, how fast items close, and whether deadlines are slipping. For a permanent record (annual review, board pack), export Excel from the Register page instead.
        </p>
      </Section>

      <Section title="Reading the dashboard">
        <Diagram
          frameLabel="Admin → KPI Dashboard"
          legend={[
            <>Set the <span className="text-white">date range</span> — figures are measured against each item's registration date.</>,
            <>Optionally use the <span className="text-white">person filter</span> (added in v2.2.0) to pin one person or compare a small group.</>,
            <><span className="text-white">Items handled</span> — how many action items the person holds now or held during the range.</>,
            <><span className="text-white">Average time-to-close</span> — the typical number of working days from registration to closing, for items the person owned.</>,
            <><span className="text-white">Breaches</span> — items closed late, or still open past their due date.</>,
          ]}
        >
          <MockRow>
            <MockSelect label="Date range" value="Last 90 days" marker={1} />
            <MockSelect label="Person" value="All people" marker={2} />
          </MockRow>
          <MockRow>
            <MockKpi label="Items handled" value="34" marker={3} />
            <MockKpi label="Sub-tasks" value="12" />
            <MockKpi label="Sub overdue" value="2" tone="warn" />
            <MockKpi label="Avg time-to-close" value="6.4d" marker={4} />
            <MockKpi label="Breaches" value="3" marker={5} tone="bad" />
          </MockRow>
        </Diagram>
      </Section>

      <Section title="What each number means">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1.5">
          <li><span className="text-white">Items handled</span> — number of action items the person currently holds or held during the date range.</li>
          <li><span className="text-white">Sub-Tasks</span> — sub-task assignments delivered to the person.</li>
          <li><span className="text-white">Sub Overdue</span> — pending sub-tasks past their due date right now.</li>
          <li><span className="text-white">Average time-to-close</span> — mean working-day time from registration to closure for items the person owned.</li>
          <li><span className="text-white">Breaches</span> — items closed past their due date or still open past due.</li>
        </ul>
      </Section>

      <Callout variant="info" title="What this tab is not">
        It is a live snapshot, not a saved record. For a permanent extract (annual review, board pack), use the Excel-export buttons on the Action Item Register and the Roles &amp; Permissions audit log — they generate timestamped <span className="text-white">.xlsx</span> files you can archive.
      </Callout>
    </div>
  );
}
